import styled from '@emotion/styled';
import { Asset } from 'contentful';
import React, { ReactNode } from 'react';
import ButtonPromptWrapper from '../ButtonPromptWrapper';
import { Video } from '../../../utils/api/videoApi';
import { breakpoints, Color, colors, spacing } from '../../../utils/styleguide';
import PrimaryButton, { ButtonColor } from '../buttons/PrimaryButton';
import Image, { ImageStyleProps } from '../../contentful-elements/Image';
import Headliner from '../Headliner';
import VideoPlayButton from '../VideoPlayButton';
import { Col, Row } from './12ColGridLayout';

const HeadlinerButton = styled(PrimaryButton)`
  border-radius: ${spacing[7]}px;
  margin: 0;
`;

type ContentProps = {
  small?: boolean;
};

const Content = styled.div<ContentProps>`
  height: 100%;
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  align-items: center;
  ${({ small }) => {
    if (small)
      return `
      padding: ${spacing[3]}px 0;
      ${breakpoints.desktop} {
        padding: ${spacing[4]}px 0;
      }`;
    return `
        padding: 38% 0;
        ${breakpoints.tablet} {
          padding: ${spacing[11]}px ${spacing[4]}px;
          min-height: 500px;
        }
        ${breakpoints.desktop} {
          padding: 140px 0;
          min-height: 720px;
        }`;
  }}
`;

const DescriptionWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${spacing[4]}px;
  ${breakpoints.desktop} {
    margin-top: ${spacing[6]}px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${spacing[6]}px;
  ${breakpoints.desktop} {
    margin-top: ${spacing[10]}px;
  }
`;

type HeadlinerSectionProps = {
  id?: string;
  title?: ReactNode;
  description?: ReactNode;
  image?: Asset;
  CTAColor?: ButtonColor;
  CTAText?: string;
  CTAPrompt?: string;
  buttonSmall?: boolean;
  href?: string;
  centered?: boolean;
  backgroundColor?: Color;
  backgroundLinear?: string;
  backgroundBlend?: string;
  small?: boolean;
  portraitVideo?: Video;
  landscapeVideo?: Video;
};

const HeadlinerLayout = ({
  id,
  title,
  description,
  image,
  CTAText,
  CTAPrompt,
  CTAColor,
  buttonSmall,
  href,
  backgroundColor,
  backgroundLinear,
  small,
  portraitVideo,
  landscapeVideo,
}: HeadlinerSectionProps) => {
  let button;

  if (portraitVideo || landscapeVideo) {
    button = (
      <VideoPlayButton
        portraitVideo={portraitVideo}
        landscapeVideo={landscapeVideo}
        buttonLabel={CTAText}
      />
    );
  } else if (href) {
    button = (
      <ButtonPromptWrapper prompt={CTAPrompt}>
        <HeadlinerButton
          externalLink
          href={href}
          size={(buttonSmall && 'small') || undefined}
          color={CTAColor || 'onLight'}
        >
          {CTAText || 'Learn more'}
        </HeadlinerButton>
      </ButtonPromptWrapper>
    );
  }

  return (
    <Headliner
      id={id}
      backgroundColor={
        (!image ? backgroundColor && colors[backgroundColor] : backgroundColor) ??
        'rgba(0, 0, 0, 0.3)'
      }
      backgroundLinear={backgroundLinear}
      Image={(props: ImageStyleProps) => <Image asset={image} {...props} />}
    >
      <Content small={small}>
        <Row>
          {title && <Col>{title}</Col>}
          {description && (
            <Col tablet={'3 / span 8'}>
              <DescriptionWrapper>{description}</DescriptionWrapper>
            </Col>
          )}
          {CTAText && (
            <Col>
              <ButtonWrapper>{button}</ButtonWrapper>
            </Col>
          )}
        </Row>
      </Content>
    </Headliner>
  );
};

export default HeadlinerLayout;
